const BASE = '/api';

// ─── Auth helpers ────────────────────────────────────────────
export function getAuthToken() {
  return localStorage.getItem('auth_token');
}

export function setAuthToken(token) {
  localStorage.setItem('auth_token', token);
}

export function clearAuth() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
  localStorage.removeItem('auth_user_obj');
}

export function getAuthUser() {
  return localStorage.getItem('auth_user');
}

// v1.10.42 — full user object from phone+code login (id/email/phone/roles/...)
// Returns null if absent or unparseable. getAuthUser() above still returns
// the display-name string for back-compat (Layout.js, Users.js).
export function getAuthUserObj() {
  const raw = localStorage.getItem('auth_user_obj');
  if (!raw) return null;
  try { return JSON.parse(raw); } catch (e) { return null; }
}

export function isAuthenticated() {
  return !!getAuthToken();
}

function authHeaders() {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

// ─── API fetch wrappers ──────────────────────────────────────
async function fetchJSON(path) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { ...authHeaders() },
  });
  if (res.status === 401) {
    clearAuth();
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

async function postJSON(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    clearAuth();
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

// Auth — v1.10.42 phone+code (replaces UN/PW)
// Step 1: send a WhatsApp code to the phone via the main AirTouch app.
export const requestCode = async (phone) => {
  const res = await fetch(`${BASE}/auth/request-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone }),
  });
  if (!res.ok) {
    let detail = '';
    try { const err = await res.json(); detail = err.detail || ''; } catch (e) {}
    throw new Error(`${res.status}: ${detail || 'request failed'}`);
  }
  return res.json();
};

// Step 2: verify the code, get our token + user object.
// clientType is 'desktop' (default) or 'mobile'; backend uses it to gate roles.
export const verifyCode = async (phone, code, clientType = 'desktop') => {
  const res = await fetch(`${BASE}/auth/verify-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, code, client_type: clientType }),
  });
  if (!res.ok) {
    let detail = '';
    try { const err = await res.json(); detail = err.detail || ''; } catch (e) {}
    throw new Error(`${res.status}: ${detail || 'login failed'}`);
  }
  const data = await res.json();
  setAuthToken(data.token);
  // Store BOTH a string display name (back-compat for Layout.js etc.)
  // and the full user object (richer data for new code).
  const u = data.user || {};
  const display = u.full_name || u.name || u.email || u.phone || '';
  localStorage.setItem('auth_user', display);
  try { localStorage.setItem('auth_user_obj', JSON.stringify(u)); } catch (e) {}
  return data;
};

export const logout = async () => {
  try { await postJSON('/auth/logout', {}); } catch(e) {}
  clearAuth();
};

// Fleet
export const getFleetSummary = () => fetchJSON('/fleet/summary');
export const getProjects = () => fetchJSON('/fleet/projects');
export const getProjectErrorSummary = (days = 7) => fetchJSON(`/fleet/projects/error-summary?days=${days}`);
export const getProjectErrorStatus = () => fetchJSON('/fleet/projects/error-status');
export const assignRobotToTechnician = (data) => postJSON('/manager/assign', data);
export const getManagerAssignments = (country = '') =>
  fetchJSON(`/manager/assignments${country ? `?country=${country}` : ''}`);
export const getClosedTickets = (country = '') =>
  fetchJSON(`/manager/closed-tickets${country ? `?country=${country}` : ''}`);
export const getRobots = (projectId) =>
  fetchJSON(`/fleet/robots${projectId ? `?project_id=${projectId}` : ''}`);

// Analysis
export const analyzeProject = (projectId, { days, hours, showAll } = {}) => {
  const parts = [];
  if (days) parts.push(`days=${days}`);
  else if (hours) parts.push(`hours=${hours}`);
  else parts.push('days=30');
  if (showAll) parts.push('show_all=true');
  return fetchJSON(`/analysis/project/${projectId}?${parts.join('&')}`);
};
export const getRowLengthCorrelation = (days = 90) =>
  fetchJSON(`/analysis/row-length-correlation?days=${days}`);

// Robot Journal
export const getRobotJournal = (projectId, robotNumber) =>
  fetchJSON(`/robot/${projectId}/${robotNumber}/journal`);

// Technician KPIs
export const getTechnicianKPIs = (days = 90) =>
  fetchJSON(`/technicians/kpi?days=${days}`);

// Errors
export const getErrorStats = (projectId, days = 30, country = '') =>
  fetchJSON(`/errors/stats?${projectId ? `project_id=${projectId}&` : ''}${country ? `country=${encodeURIComponent(country)}&` : ''}days=${days}`);
export const getCategoryDetail = (category, days = 30) =>
  fetchJSON(`/errors/category-detail/${encodeURIComponent(category)}?days=${days}`);

// Parts
export const getPartsCatalog = () => fetchJSON('/parts/catalog');
export const getPartsForError = (errorCode) =>
  fetchJSON(`/parts/for-error/${errorCode}`);

// Health
export const getHealth = () => fetchJSON('/health');

// Site-level alerts (BLE_ERROR / OFFLINE) per project
export const getSiteAlerts = () => fetchJSON('/fleet/projects/site-alerts');

// Weekly Trends (home-page analytics)
export const getWeeklyTrends = () => fetchJSON('/trends/weekly');
export const refreshWeeklyTrends = () => postJSON('/trends/weekly/refresh', {});

// Smart Analysis cache — Assignment page uses these for the RCA column
export const getRcaRefreshStatus = (projectId, robotNumbers) =>
  fetchJSON(`/analysis/project/${projectId}/rca-refresh-status?robots=${robotNumbers.join(',')}`);
export const forceRcaRefresh = (projectId, robotNumber) =>
  postJSON(`/analysis/project/${projectId}/rca-refresh/${robotNumber}`, {});

// --- Technician Mobile App ---
export const startTechDay = (data) => postJSON('/technician/start-day', data);
export const getTechTasks = (techId, statusFilter) =>
  fetchJSON(`/technician/tasks/${techId}${statusFilter ? `?status_filter=${statusFilter}` : ''}`);
export const getTechTaskDetail = (taskId, language = 'en-US') =>
  fetchJSON(`/technician/task/${taskId}?language=${language}`);
export const startTechTask = (data) => postJSON('/technician/task/start', data);
export const completeTechTask = (data) => postJSON('/technician/task/complete', data);
export const escalateTechTask = (data) => postJSON('/technician/task/escalate', data);
export const uploadTaskPhoto = async (taskId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BASE}/technician/task/${taskId}/photo`, { method: 'POST', body: formData, headers: authHeaders() });
  if (res.status === 401) { clearAuth(); window.location.href = '/login'; throw new Error('Session expired'); }
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
};
export const getTechInterview = (taskId, language = 'en-US') =>
  fetchJSON(`/technician/interview/${taskId}?language=${language}`);
export const endTechDay = (techId, language = 'en-US') =>
  fetchJSON(`/technician/end-day/${techId}?language=${language}`);
export const techSafetyCheck = (techId, language = 'en-US') =>
  fetchJSON(`/technician/safety-check?technician_id=${techId}&language=${language}`);
export const seedDemoTasks = (techId, projectName = 'Lotan', language = 'he-IL') =>
  postJSON(`/technician/seed-demo?technician_id=${techId}&project_name=${encodeURIComponent(projectName)}&language=${language}`, {});

// --- Technician Management (Dashboard) ---
export const getTechnicians = (projectId) =>
  fetchJSON(`/technician-mgmt/technicians${projectId ? `?project_id=${projectId}` : ''}`);
export const createTechnician = (data) => postJSON('/technician-mgmt/technicians', data);
export const updateTechnician = async (id, data) => {
  const res = await fetch(`${BASE}/technician-mgmt/technicians/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(data),
  });
  if (res.status === 401) { clearAuth(); window.location.href = '/login'; throw new Error('Session expired'); }
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
};
export const deleteTechnician = async (id) => {
  const res = await fetch(`${BASE}/technician-mgmt/technicians/${id}`, { method: 'DELETE', headers: authHeaders() });
  if (res.status === 401) { clearAuth(); window.location.href = '/login'; throw new Error('Session expired'); }
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
};
export const importTechnicianCSV = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BASE}/technician-mgmt/technicians/import-csv`, { method: 'POST', body: formData, headers: authHeaders() });
  if (res.status === 401) { clearAuth(); window.location.href = '/login'; throw new Error('Session expired'); }
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
};
export const getProjectsForDropdown = () => fetchJSON('/technician-mgmt/projects-list');

// Logistics
export const getLogisticsCenters = () => fetchJSON('/logistics/centers');
export const getCenterDetail = (centerId) => fetchJSON(`/logistics/centers/${centerId}`);
export const getReorderAlerts = () => fetchJSON('/logistics/alerts');
export const getTransferSuggestions = () => fetchJSON('/logistics/transfers');
export const getConsumptionForecast = (centerId, days = 30) =>
  fetchJSON(`/logistics/consumption/${centerId}?days=${days}`);
export const findNearestCenter = (lat, lon) =>
  fetchJSON(`/logistics/nearest?lat=${lat}&lon=${lon}`);
export const planRoute = (data) => postJSON('/logistics/plan-route', data);

// A/B Testing
export const getABTests = (status) =>
  fetchJSON(`/ab-tests${status ? `?status=${status}` : ''}`);
export const getTestSuggestions = () => fetchJSON('/ab-tests/suggestions');
export const getABTest = (testId) => fetchJSON(`/ab-tests/${testId}`);
export const createABTest = (data) => postJSON('/ab-tests', data);
export const startABTest = (testId) => postJSON(`/ab-tests/${testId}/start`, {});
export const pauseABTest = (testId) => postJSON(`/ab-tests/${testId}/pause`, {});
export const stopABTest = (testId) => postJSON(`/ab-tests/${testId}/stop`, {});
export const analyzeABTest = (testId) => fetchJSON(`/ab-tests/${testId}/analyze`);

// Regions
export const getRegions = () => fetchJSON('/regions');
export const getRegionDetail = (regionId) => fetchJSON(`/regions/${regionId}`);
export const validateRegion = (regionId) => fetchJSON(`/regions/${regionId}/validate`);
export const getAuditLog = (limit = 100) => fetchJSON(`/regions/audit/log?limit=${limit}`);

// Training
export const getCurriculum = () => fetchJSON('/training/curriculum');
export const getTrainees = () => fetchJSON('/training/trainees');
export const getTraineeDashboard = (techId) => fetchJSON(`/training/trainees/${techId}`);
export const enrollTechnician = (data) => postJSON('/training/enroll', data);
export const startTrainingModule = (techId, moduleId) =>
  postJSON(`/training/trainees/${techId}/start/${moduleId}`, {});
export const completeTrainingStep = (techId, moduleId, stepId, score) =>
  postJSON(`/training/trainees/${techId}/complete/${moduleId}/${stepId}`, { score });
export const getExpiringCerts = (days = 30) =>
  fetchJSON(`/training/certifications/expiring?days=${days}`);

// Robot Summary (per-robot history page)
// NOTE: these endpoints are not implemented yet on the backend — stubbed so the
// RobotSummary.js page compiles. They will 404 at runtime until the backend is added.
export const getProjectRobotsSummary = (projectId) =>
  fetchJSON(`/robot-summary/project/${projectId}`);
export const getRobotSummary = (robotNumber, days = 90, projectId = '') =>
  fetchJSON(`/robot-summary/robot/${robotNumber}?days=${days}${projectId ? `&project_id=${projectId}` : ''}`);
// v1.10.4 Amendment J — accepts optional Assignment-page round-trip params.
// When fromAssignment=true, the response will include from_assignment:true
// and assignment_return_url so the page can render a "← Return to Assignment
// Page" button that preserves filter state + scroll anchor.
export const getRobotAnalysis = (robotNumber, projectId = '', opts = {}) => {
  const parts = [];
  if (projectId) parts.push(`project_id=${projectId}`);
  if (opts.fromAssignment) parts.push('from=assignment');
  if (opts.returnFilter) parts.push(`return_filter=${encodeURIComponent(opts.returnFilter)}`);
  if (opts.returnAnchor) parts.push(`return_anchor=${encodeURIComponent(opts.returnAnchor)}`);
  const qs = parts.length ? `?${parts.join('&')}` : '';
  return fetchJSON(`/robot-summary/robot/${robotNumber}/analysis${qs}`);
};
export const getRobotRCA = (robotNumber, projectId = '', days = 30) =>
  fetchJSON(`/robot-summary/robot/${robotNumber}/rca?days=${days}${projectId ? `&project_id=${projectId}` : ''}`);
export const getRobotBriefingDetails = (robotNumber, projectId = '') =>
  fetchJSON(`/robot-summary/robot/${robotNumber}/briefing${projectId ? `?project_id=${projectId}` : ''}`);
export const saveFieldFinding = (data) => postJSON('/robot-summary/findings', data);
export const getRobotFindings = (robotNumber) =>
  fetchJSON(`/robot-summary/robot/${robotNumber}/findings`);
export const uploadFindingPhoto = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return fetch(`${BASE}/robot-summary/findings/upload-photo`, {
    method: 'POST',
    headers: authHeaders(),
    body: fd,
  }).then(r => r.ok ? r.json() : Promise.reject(new Error(`Upload failed: ${r.status}`)));
};

// ─── Analytical Reports (Changes Impact Analysis) ────────────
export const getAnalyticalReports = (weeks = 4) =>
  fetchJSON(`/analytical-reports?weeks=${weeks}`);

export const downloadAnalyticalReport = (id) => {
  // Stream the .docx via an authenticated request, then trigger a
  // browser-native download with the server-provided filename.
  return fetch(`${BASE}/analytical-reports/${id}/download`, {
    headers: { ...authHeaders() },
  }).then(async (res) => {
    if (res.status === 401) { clearAuth(); window.location.href = '/login'; throw new Error('Session expired'); }
    if (!res.ok) throw new Error(`Download failed: ${res.status}`);
    // Try to read the filename from Content-Disposition
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
    const filename = m ? decodeURIComponent(m[1]) : `report_${id}.docx`;
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 100);
  });
};

export const getAnalyticalSuggestions = (status = '') =>
  fetchJSON(`/analytical-reports/suggestions${status ? `?status=${status}` : ''}`);

export const submitAnalyticalSuggestion = (text) =>
  postJSON('/analytical-reports/suggestions', { text });

export const editAnalyticalSuggestion = async (id, text) => {
  const res = await fetch(`${BASE}/analytical-reports/suggestions/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ text }),
  });
  if (res.status === 401) { clearAuth(); window.location.href = '/login'; throw new Error('Session expired'); }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Edit failed: ${res.status}`);
  }
  return res.json();
};

// ─────────────────────────────────────────────────────────────────────────
// v1.10.8 (2026-05-09) — User Management + Inventory Dashboard + DE flow
// ─────────────────────────────────────────────────────────────────────────

async function patchJSON(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (res.status === 401) { clearAuth(); window.location.href = '/login'; throw new Error('Session expired'); }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `API error: ${res.status}`);
  }
  return res.json();
}

async function deleteJSON(path) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'DELETE',
    headers: { ...authHeaders() },
  });
  if (res.status === 401) { clearAuth(); window.location.href = '/login'; throw new Error('Session expired'); }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `API error: ${res.status}`);
  }
  return res.json();
}

// Users CRUD
export const getUsers = ({ role, region, site, country, includeInactive } = {}) => {
  const parts = [];
  if (role) parts.push(`role=${encodeURIComponent(role)}`);
  if (region) parts.push(`region=${encodeURIComponent(region)}`);
  if (site) parts.push(`site=${encodeURIComponent(site)}`);
  if (country) parts.push(`country=${encodeURIComponent(country)}`);
  if (includeInactive) parts.push('include_inactive=true');
  const qs = parts.length ? `?${parts.join('&')}` : '';
  return fetchJSON(`/users${qs}`);
};
export const getUser = (userId) => fetchJSON(`/users/${userId}`);
export const createUser = (body) => postJSON('/users', body);
export const updateUser = (userId, patch) => patchJSON(`/users/${userId}`, patch);
export const deleteUser = (userId) => deleteJSON(`/users/${userId}`);

// v1.10.42 — access_control CRUD (admin-only, enforced on the backend).
export const listAccessControl = (role = '') => {
  const qs = role ? `?role=${encodeURIComponent(role)}` : '';
  return fetchJSON(`/admin/access-control${qs}`);
};
export const grantAccess = (body) => postJSON('/admin/access-control', body);
export const updateAccess = (phone, patch) =>
  fetch(`${BASE}/admin/access-control/${encodeURIComponent(phone)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(patch),
  }).then(async r => {
    if (r.status === 401) { clearAuth(); window.location.href = '/login'; throw new Error('Session expired'); }
    if (!r.ok) {
      let detail = '';
      try { const e = await r.json(); detail = e.detail || ''; } catch (e) {}
      throw new Error(`${r.status}: ${detail || 'update failed'}`);
    }
    return r.json();
  });
export const revokeAccess = (phone) =>
  deleteJSON(`/admin/access-control/${encodeURIComponent(phone)}`);

// Mobile login (email + phone) — used by the technician mobile app
export const mobileLogin = (email, phone) => postJSON('/auth/mobile-login', { email, phone });
// Phase B persistent assignments (v1.10.2 schema, v1.10.11 mobile-aligned)
export const listAssignments = ({ status, technician_id, project_id, limit } = {}) => {
  const parts = [];
  if (status) parts.push(`status=${encodeURIComponent(status)}`);
  if (technician_id) parts.push(`technician_id=${encodeURIComponent(technician_id)}`);
  if (project_id) parts.push(`project_id=${encodeURIComponent(project_id)}`);
  if (limit) parts.push(`limit=${limit}`);
  const qs = parts.length ? `?${parts.join('&')}` : '';
  return fetchJSON(`/assignments${qs}`);
};


// Inventory dashboard
export const getInventoryFleetTotals = () => fetchJSON('/inventory/fleet-totals');
export const getInventoryHoldings = () => fetchJSON('/inventory/holdings');
export const getInventoryConsumption = ({ days = 30, site, robot, techId } = {}) => {
  const parts = [`days=${days}`];
  if (site) parts.push(`site=${encodeURIComponent(site)}`);
  if (robot != null) parts.push(`robot=${robot}`);
  if (techId) parts.push(`tech_id=${encodeURIComponent(techId)}`);
  return fetchJSON(`/inventory/consumption?${parts.join('&')}`);
};

// DE flow
export const createDE = (body) => postJSON('/de', body);
export const getIncomingDE = (userId, since) => {
  const parts = [];
  if (userId) parts.push(`user_id=${encodeURIComponent(userId)}`);
  if (since) parts.push(`since=${encodeURIComponent(since)}`);
  const qs = parts.length ? `?${parts.join('&')}` : '';
  return fetchJSON(`/de/incoming${qs}`);
};
export const confirmDE = (deId) => postJSON(`/de/${deId}/confirm`, {});
export const rejectDE = (deId) => postJSON(`/de/${deId}/reject`, {});


// ─── v1.10.44 Field Feedback ─────────────────────────────────
function _ffQS({ country, state, site, error_code, status } = {}) {
  const qs = new URLSearchParams();
  if (country && country !== 'All') qs.set('country', country);
  if (state && state !== 'All') qs.set('state', state);
  if (site && site !== 'All') qs.set('site', site);
  if (error_code && error_code !== 'All') qs.set('error_code', error_code);
  if (status && status !== 'All') qs.set('status', status);
  const qstr = qs.toString();
  return qstr ? `?${qstr}` : '';
}

export const getFieldFeedback = (filters = {}) =>
  fetchJSON(`/field-feedback${_ffQS(filters)}`);

export const downloadFieldFeedbackExport = (filters = {}) =>
  fetch(`${BASE}/field-feedback/export${_ffQS(filters)}`, { headers: { ...authHeaders() } })
    .then(async (res) => {
      if (res.status === 401) { clearAuth(); window.location.href = '/login'; throw new Error('Session expired'); }
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
      const filename = m ? decodeURIComponent(m[1]) : 'field_feedback.xlsx';
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    });

// ─── v1.10.53 Robot control buttons (proxy via Desktop backend) ──────────
// robotId = the robot's main-app Mongo _id (NOT robot_number).
export const robotCommand = (robotId, projectId, event) =>
  postJSON('/robot/command', { robotId, projectId, event });
export const robotBatteryRefresh = (robotId, projectId) =>
  postJSON('/robot/battery-refresh', { robotId, projectId });
export const robotBatteryLiveReport = (projectId, processId) =>
  fetchJSON(`/robot/battery-livereport?projectId=${encodeURIComponent(projectId)}&processId=${encodeURIComponent(processId)}`);
export const robotBatteryStore = (reading) =>
  postJSON('/robot/battery-reading', reading);
export const robotBatteryHistory = (robotId, limit = 4, projectId, robotNumber) =>
  fetchJSON(`/robot/${encodeURIComponent(robotId)}/battery-history?limit=${limit}`
    + (projectId ? `&projectId=${encodeURIComponent(projectId)}` : '')
    + (robotNumber != null ? `&robotNumber=${encodeURIComponent(robotNumber)}` : ''));
